import { useMemo, useState } from "react";
import { Plus, Search, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, fmtEuro, fmtDate, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { useSearchableList } from "@/hooks/useSearchableList";
import { PageHeader, Field, EmptyState, inputCls, splitName, RefBadge, SortableTh } from "@/components/shared";
import PhoneField from "@/components/PhoneField";
import Timeline from "@/components/Timeline";

const emptyBuyer = {
  name: "", first_name: "", last_name: "", email: "", phone: "", country: "", budget_min: "", budget_max: "",
  finance_position: "unknown", search_area: "", must_haves: "", deal_breakers: "",
  likes_dislikes: "", next_action: "", follow_up_date: "", notes: "",
};

export default function Buyers() {
  const { items, search, setSearch, reload } = useSearchableList("/buyers");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const { t, lang } = useLang();

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const sorted = useMemo(() => {
    const list = [...items];
    const dir = sortDir === "asc" ? 1 : -1;
    const val = (b) => {
      if (sortKey === "budget") return b.budget_max || b.budget_min || 0;
      if (sortKey === "follow_up_date") return b.follow_up_date || "";
      return (b[sortKey] || "").toString().toLowerCase();
    };
    list.sort((a, b) => (val(a) > val(b) ? dir : val(a) < val(b) ? -dir : 0));
    return list;
  }, [items, sortKey, sortDir]);

  const save = async () => {
    const fullName = `${form.first_name || ""} ${form.last_name || ""}`.trim();
    if (!fullName) return toast.error(t("required"));
    setSaving(true);
    const payload = {
      ...form,
      name: fullName,
      budget_min: form.budget_min === "" || form.budget_min == null ? null : Number(form.budget_min),
      budget_max: form.budget_max === "" || form.budget_max == null ? null : Number(form.budget_max),
    };
    try {
      if (form.id) await api.put(`/buyers/${form.id}`, payload);
      else await api.post("/buyers", payload);
      toast.success(t("saved"));
      setForm(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(t("confirmDelete"))) return;
    await api.delete(`/buyers/${form.id}`);
    toast.success(t("deleted"));
    setForm(null);
    reload();
  };

  const financeLabel = { cash: t("cash"), mortgage: t("mortgage"), property_to_sell: t("propertyToSell"), unknown: t("unknown") };

  return (
    <div data-testid="buyers-page">
      <PageHeader
        title={t("buyers")}
        action={
          <button data-testid="add-buyer-button" onClick={() => setForm({ ...emptyBuyer })}
            className="inline-flex items-center gap-2 bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2.5 text-sm font-medium transition-colors">
            <Plus size={16} strokeWidth={1.5} /> {t("addBuyer")}
          </button>
        }
      />
      <div className="relative mb-6 max-w-md">
        <Search size={16} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <input data-testid="buyers-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder={t("search")} className={`${inputCls} pl-9`} />
      </div>

      {items.length === 0 ? <EmptyState message={t("noResults")} /> : (
        <div className="bg-white border border-stone-200 rounded-xl overflow-hidden" data-testid="buyers-table-wrap">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wider text-stone-500">
                <tr>
                  <SortableTh onClick={() => toggleSort("ref_code")} active={sortKey === "ref_code"} dir={sortDir}>{t("ref")}</SortableTh>
                  <SortableTh onClick={() => toggleSort("name")} active={sortKey === "name"} dir={sortDir}>{t("name")}</SortableTh>
                  <SortableTh onClick={() => toggleSort("country")} active={sortKey === "country"} dir={sortDir}>{t("country")}</SortableTh>
                  <SortableTh onClick={() => toggleSort("budget")} active={sortKey === "budget"} dir={sortDir} align="right">{t("budget")}</SortableTh>
                  <SortableTh onClick={() => toggleSort("search_area")} active={sortKey === "search_area"} dir={sortDir}>{t("searchArea")}</SortableTh>
                  <SortableTh onClick={() => toggleSort("finance_position")} active={sortKey === "finance_position"} dir={sortDir}>{t("financePosition")}</SortableTh>
                  <SortableTh onClick={() => toggleSort("follow_up_date")} active={sortKey === "follow_up_date"} dir={sortDir}>{t("nextAction")}</SortableTh>
                  <th className="text-right py-2.5 px-3 font-medium w-16"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {sorted.map((b) => (
                  <tr
                    key={b.id}
                    data-testid={`buyer-row-${b.id}`}
                    onClick={() => setForm({ ...emptyBuyer, ...b, ...splitName(b) })}
                    className="hover:bg-stone-50 cursor-pointer transition-colors"
                  >
                    <td className="py-2.5 px-3"><RefBadge code={b.ref_code} /></td>
                    <td className="py-2.5 px-3 font-medium text-stone-900">{b.name}</td>
                    <td className="py-2.5 px-3 text-stone-600">{b.country || "—"}</td>
                    <td className="py-2.5 px-3 text-stone-700 text-right tabular-nums whitespace-nowrap">
                      {(b.budget_min || b.budget_max) ? `${fmtEuro(b.budget_min)} – ${fmtEuro(b.budget_max)}` : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-stone-600 max-w-[180px] truncate">{b.search_area || "—"}</td>
                    <td className="py-2.5 px-3">
                      <span className="bg-stone-100 text-stone-600 border border-stone-200 rounded-full px-2.5 py-0.5 text-[11px] font-medium whitespace-nowrap">
                        {financeLabel[b.finance_position] || b.finance_position}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-stone-600 max-w-[220px]">
                      {b.next_action ? (
                        <div className="truncate">
                          <span className="text-[#9A3412]">→ {b.next_action}</span>
                          {b.follow_up_date && <span className="text-stone-400 ml-1">({fmtDate(b.follow_up_date, lang)})</span>}
                        </div>
                      ) : "—"}
                    </td>
                    <td className="py-2.5 px-3 text-right">
                      <Pencil size={14} strokeWidth={1.5} className="text-stone-300 inline-block" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <DialogContent className="max-w-2xl bg-white">
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl font-medium text-stone-900 flex items-center gap-3 flex-wrap">
                  <span>{form.id ? t("editBuyer") : t("addBuyer")}</span>
                  <RefBadge code={form.ref_code} size="md" />
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                <Field label={t("firstName")}><input data-testid="buyer-firstname-input" className={inputCls} value={form.first_name} onChange={set("first_name")} /></Field>
                <Field label={t("surname")}><input data-testid="buyer-surname-input" className={inputCls} value={form.last_name} onChange={set("last_name")} /></Field>
                <Field label={t("country")}><input data-testid="buyer-country-input" className={inputCls} value={form.country} onChange={set("country")} /></Field>
                <Field label={t("email")}><input data-testid="buyer-email-input" className={inputCls} value={form.email} onChange={set("email")} /></Field>
                <Field label={t("phone")}><PhoneField testId="buyer-phone-input" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} /></Field>
                <Field label={t("dateOfBirth")}><input data-testid="buyer-dob-input" type="date" className={inputCls} value={form.date_of_birth || ""} onChange={set("date_of_birth")} /></Field>
                <Field label={t("nationality")}><input data-testid="buyer-nationality-input" className={inputCls} value={form.nationality || ""} onChange={set("nationality")} /></Field>
                <Field label={t("placeOfBirth")}><input data-testid="buyer-pob-input" className={inputCls} value={form.place_of_birth || ""} onChange={set("place_of_birth")} /></Field>
                <Field label={t("countryOfBirth")}><input data-testid="buyer-cob-input" className={inputCls} value={form.country_of_birth || ""} onChange={set("country_of_birth")} /></Field>
                <Field label={`${t("budget")} min (€)`}><input data-testid="buyer-budget-min-input" type="number" className={inputCls} value={form.budget_min ?? ""} onChange={set("budget_min")} /></Field>
                <Field label={`${t("budget")} max (€)`}><input data-testid="buyer-budget-max-input" type="number" className={inputCls} value={form.budget_max ?? ""} onChange={set("budget_max")} /></Field>
                <Field label={t("financePosition")}>
                  <Select value={form.finance_position} onValueChange={(v) => setForm({ ...form, finance_position: v })}>
                    <SelectTrigger data-testid="buyer-finance-select" className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">{t("cash")}</SelectItem>
                      <SelectItem value="mortgage">{t("mortgage")}</SelectItem>
                      <SelectItem value="property_to_sell">{t("propertyToSell")}</SelectItem>
                      <SelectItem value="unknown">{t("unknown")}</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
                <Field label={t("followUpDate")}><input data-testid="buyer-followup-input" type="date" className={inputCls} value={form.follow_up_date} onChange={set("follow_up_date")} /></Field>
                <Field label={t("searchArea")} className="sm:col-span-2"><input data-testid="buyer-area-input" className={inputCls} value={form.search_area} onChange={set("search_area")} /></Field>
                <Field label={t("mustHaves")}><textarea data-testid="buyer-musthaves-input" rows={2} className={inputCls} value={form.must_haves} onChange={set("must_haves")} /></Field>
                <Field label={t("dealBreakers")}><textarea data-testid="buyer-dealbreakers-input" rows={2} className={inputCls} value={form.deal_breakers} onChange={set("deal_breakers")} /></Field>
                <Field label={t("likesDislikes")} className="sm:col-span-2"><textarea rows={2} className={inputCls} value={form.likes_dislikes} onChange={set("likes_dislikes")} /></Field>
                <Field label={t("nextAction")} className="sm:col-span-2">
                  <input data-testid="buyer-nextaction-input" className={inputCls} value={form.next_action} onChange={set("next_action")} />
                  <p className="text-[11px] text-stone-400 mt-1">{t("autoTaskHint")}</p>
                </Field>
                <Field label={t("notes")} className="sm:col-span-2"><textarea data-testid="buyer-notes-input" rows={3} className={inputCls} value={form.notes} onChange={set("notes")} /></Field>
              </div>
              {form.id && (
                <div className="mt-8 pt-6 border-t border-stone-200">
                  <h3 className="font-heading text-lg font-medium text-stone-900 mb-4">{t("timelineTab")}</h3>
                  <Timeline linkKey="buyer_id" linkId={form.id} linkName={form.name} />
                </div>
              )}
              <div className="flex items-center justify-between mt-6">
                {form.id ? (
                  <button data-testid="delete-buyer-button" onClick={remove} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#7F1D1D] hover:underline">
                    <Trash2 size={14} strokeWidth={1.5} /> {t("delete")}
                  </button>
                ) : <span />}
                <div className="flex gap-3">
                  <button data-testid="cancel-buyer-button" onClick={() => setForm(null)} className="bg-white text-stone-700 border border-stone-200 hover:bg-stone-50 rounded-lg px-5 py-2 text-sm font-medium transition-colors">{t("cancel")}</button>
                  <button data-testid="save-buyer-button" onClick={save} disabled={saving} className="bg-slate-900 text-white hover:bg-slate-800 rounded-lg px-5 py-2 text-sm font-medium transition-colors disabled:opacity-60">{t("save")}</button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
